// Cards of a view (um/2026-10-30/03 п.4, «плашки с аналитикой» as in ITConf): one large card per record — its title, labels,
// the numbers of the view's columns as figures and the rest as lines; the same query as the table, a click opens the record.

import { memo, useState } from 'react';
import { recordTitle, type FieldDef, type Id, type RecordObj, type ViewDef } from '@confdeck/core-shared';
import { FieldValue } from '../fields/FieldValue.tsx';
import { useMeta, useRecord, useTypesTick } from '../sync/react.tsx';
import { Button } from '../ui/primitives.tsx';
import s from './cards.module.css';

const LABELS = new Set(['taxonomy', 'checkbox']);
const FIGURES = new Set(['number', 'money']);
/** Cards are heavy: they come in pages, «Показать ещё» adds the next one. */
const PAGE = 300;

export function Cards({ typeId, view, ids, onOpen }: { typeId: Id; view: ViewDef; ids: Id[]; onOpen: (id: Id, e?: React.MouseEvent) => void }) {
  const meta = useMeta();
  const [limit, setLimit] = useState(PAGE);
  // Linked records named on a card (the series of an event) come with their own type.
  const fields = view.columns.filter((c) => !c.hidden && c.field !== meta.type(typeId)?.titleField).map((c) => meta.field(typeId, c.field)).filter((f): f is FieldDef => !!f && !f.archivedAt);
  useTypesTick([typeId, ...fields.filter((f) => f.fieldType === 'relation').map((f) => meta.relationTarget(f)?.typeId).filter((t): t is Id => !!t)]);
  const parts = { labels: fields.filter((f) => LABELS.has(f.fieldType)), figures: fields.filter((f) => FIGURES.has(f.fieldType)), lines: fields.filter((f) => !LABELS.has(f.fieldType) && !FIGURES.has(f.fieldType)) };
  return (
    <div className={s.cards}>
      {ids.slice(0, limit).map((id) => <Card key={id} id={id} parts={parts} onOpen={onOpen} />)}
      {ids.length > limit && <div className={s.more}>Показаны {limit.toLocaleString('ru-RU')} из {ids.length.toLocaleString('ru-RU')} <Button size="sm" variant="ghost" onClick={() => setLimit((n) => n + PAGE)}>Показать ещё</Button></div>}
    </div>
  );
}

type Parts = { labels: FieldDef[]; figures: FieldDef[]; lines: FieldDef[] };
const empty = (rec: RecordObj, f: FieldDef) => f.fieldType !== 'relation' && (rec.data[f.key] == null || rec.data[f.key] === '' || rec.data[f.key] === false);

const Card = memo(function Card({ id, parts, onOpen }: { id: Id; parts: Parts; onOpen: (id: Id, e?: React.MouseEvent) => void }) {
  const meta = useMeta();
  const rec = useRecord(id);
  if (!rec) return null;
  return (
    <article className={s.card} tabIndex={0} onClick={(e) => onOpen(id, e)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(id); }} aria-label={recordTitle(rec, meta)}>
      <div className={s.head}>
        <h3 className={s.title}>{recordTitle(rec, meta)}</h3>
        <div className={s.labels}>{parts.labels.filter((f) => !empty(rec, f)).map((f) => <span key={f.id} title={f.name}><FieldValue rec={rec} field={f} /></span>)}</div>
      </div>
      {parts.figures.length > 0 && (
        <dl className={s.figures}>
          {parts.figures.map((f) => <div key={f.id} className={s.figure}><dt>{f.name}</dt><dd>{empty(rec, f) ? '0' : <FieldValue rec={rec} field={f} />}</dd></div>)}
        </dl>
      )}
      {parts.lines.some((f) => !empty(rec, f)) && (
        <dl className={s.lines}>
          {parts.lines.filter((f) => !empty(rec, f)).map((f) => <div key={f.id} className={s.line}><dt>{f.name}</dt><dd><FieldValue rec={rec} field={f} /></dd></div>)}
        </dl>
      )}
    </article>
  );
});
